import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { NavBar } from '../components/NavBar';
import { toast } from '../stores/toast.store';

interface Video {
  id: string;
  topicId: string | null;
  topicTitle: string;
  /** 'heygen_import' = picked from the HeyGen library (no script → can't be regenerated). */
  source?: 'generated' | 'heygen_import';
  status: 'pending' | 'generating' | 'downloading' | 'ready' | 'approved' | 'published' | 'error' | 'timeout';
  aspectRatio: '9:16' | '16:9' | '1:1';
  durationSeconds: number | null;
  thumbnailUrl: string | null;
  streamUrl: string | null;
  isApproved: boolean;
  pollAttempts: number;
  errorMessage: string | null;
  createdAt: string;
}

const STATUS: Record<Video['status'], { label: string; cls: string }> = {
  pending:     { label: 'В очереди',       cls: 'badge-slate' },
  generating:  { label: 'Генерируется',    cls: 'badge-amber' },
  downloading: { label: 'Скачивание',      cls: 'badge-indigo' },
  ready:       { label: 'Готово',           cls: 'badge-green' },
  approved:    { label: 'Одобрено',         cls: 'badge-violet' },
  published:   { label: 'Опубликовано',     cls: 'badge-green' },
  error:       { label: 'Ошибка',           cls: 'badge-red' },
  timeout:     { label: 'Таймаут',          cls: 'badge-red' },
};

type Platform = 'instagram' | 'tiktok' | 'youtube' | 'linkedin' | 'telegram' | 'facebook';

const ALL_PLATFORMS: Array<{ key: Platform; icon: string; label: string }> = [
  { key: 'instagram', icon: '📸', label: 'Instagram' },
  { key: 'facebook',  icon: '👍', label: 'Facebook' },
  { key: 'tiktok',    icon: '🎵', label: 'TikTok' },
  { key: 'youtube',   icon: '▶️', label: 'YouTube' },
  { key: 'linkedin',  icon: '💼', label: 'LinkedIn' },
  { key: 'telegram',  icon: '✈️', label: 'Telegram' },
];

export default function VideoReview() {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const [videos, setVideos] = useState<Video[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'error'>('pending');

  // Scheduling modal state
  const [scheduleVideoId, setScheduleVideoId] = useState<string | null>(null);
  const [pickedPlatforms, setPickedPlatforms] = useState<Set<Platform>>(new Set());
  const [scheduleWhen, setScheduleWhen] = useState<string>('');
  const [availablePlatforms, setAvailablePlatforms] = useState<Set<Platform>>(new Set());

  const load = () => api.get('/videos').then((r) => setVideos(r.data.videos)).catch(() => {});

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    load();
    const id = setInterval(load, 5000);
    // Determine which platforms the tenant has profiles for (via PostProxy map or active native rows)
    api.get('/settings/social').then((r) => {
      const set = new Set<Platform>();
      for (const c of r.data?.connections ?? []) {
        if (c.platform === 'postproxy' && c.isActive) {
          const map = c.metadata?.postproxy_profiles ?? {};
          for (const k of Object.keys(map)) if (ALL_PLATFORMS.some(p => p.key === k)) set.add(k as Platform);
        }
        if (c.isActive && ALL_PLATFORMS.some(p => p.key === c.platform)) set.add(c.platform as Platform);
      }
      setAvailablePlatforms(set);
    }).catch(() => {});
    return () => clearInterval(id);
  }, [user, navigate]);

  async function approve(id: string) {
    await api.post(`/videos/${id}/approve`); toast.success('Видео одобрено'); load();
  }
  async function regenerate(id: string) {
    if (!confirm('Перегенерировать? Текущий файл удалится.')) return;
    await api.post(`/videos/${id}/regenerate`); toast.info('Перегенерируется'); load();
  }
  async function remove(id: string) {
    if (!confirm('Удалить видео?')) return;
    await api.delete(`/videos/${id}`); load();
  }
  function openScheduleModal(id: string) {
    // Pre-check all available platforms; "сейчас" as default time
    setPickedPlatforms(new Set(availablePlatforms));
    // Default to local now in datetime-local format
    const now = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000)
      .toISOString().slice(0, 16);
    setScheduleWhen(now);
    setScheduleVideoId(id);
  }
  function togglePlatform(p: Platform) {
    setPickedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  }
  async function submitSchedule() {
    if (!scheduleVideoId) return;
    if (pickedPlatforms.size === 0) { toast.error('Выберите минимум 1 платформу'); return; }
    try {
      await api.post('/publications', {
        videoId: scheduleVideoId,
        platforms: Array.from(pickedPlatforms),
        scheduledAt: scheduleWhen ? new Date(scheduleWhen).toISOString() : new Date().toISOString(),
        caption: '', hashtags: [],
      });
      toast.success(`В очередь публикации (${pickedPlatforms.size} платформ${pickedPlatforms.size === 1 ? '' : 'ы'})`);
      setScheduleVideoId(null);
      setTimeout(() => navigate('/calendar'), 800);
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Ошибка');
    }
  }

  const counts = {
    all: videos.length,
    pending: videos.filter((v) => !v.isApproved && v.status === 'ready').length,
    approved: videos.filter((v) => v.isApproved).length,
    error: videos.filter((v) => v.status === 'error' || v.status === 'timeout').length,
  };
  const filtered = videos.filter((v) =>
    filter === 'all' ? true :
    filter === 'pending' ? !v.isApproved && v.status === 'ready' :
    filter === 'approved' ? v.isApproved :
    v.status === 'error' || v.status === 'timeout'
  );

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6 pb-16">
        <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Просмотр видео</h1>
            <p className="text-slate-500 mt-1.5">HeyGen генерирует 5–10 минут. Одобрение → планирование публикации.</p>
          </div>
          <button onClick={() => navigate('/heygen-library')} className="btn btn-md btn-primary whitespace-nowrap">
            🎞 Библиотека HeyGen
          </button>
        </header>

        <div className="inline-flex card-glass p-1 mb-6 gap-1">
          {([
            { key: 'pending',  label: 'На проверку', n: counts.pending },
            { key: 'approved', label: 'Одобрены',    n: counts.approved },
            { key: 'error',    label: 'Ошибки',      n: counts.error },
            { key: 'all',      label: 'Все',         n: counts.all },
          ] as const).map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filter === f.key
                  ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md'
                  : 'text-slate-600 hover:bg-white/60'
              }`}
            >
              {f.label} <span className="ml-2 opacity-70 text-xs">{f.n}</span>
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="card-glass p-12 text-center">
            <div className="text-5xl mb-3">🎬</div>
            <h3 className="font-semibold mb-1">Пока нет видео</h3>
            <p className="text-sm text-slate-500 mb-4">Одобри скрипт — нажми «🎬 Создать видео».</p>
            <button onClick={() => navigate('/scripts')} className="btn btn-md btn-primary">
              ✍️ К скриптам
            </button>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((v) => {
            const thumb = v.thumbnailUrl ? `${v.thumbnailUrl}?t=${encodeURIComponent(token ?? '')}` : null;
            const st = STATUS[v.status];
            return (
              <div key={v.id} className="card-glass card-glass-hover overflow-hidden">
                <div className={`${v.aspectRatio === '9:16' ? 'aspect-[9/16]' : v.aspectRatio === '16:9' ? 'aspect-video' : 'aspect-square'} bg-slate-900 relative`}>
                  {v.streamUrl ? (
                    <video
                      controls
                      preload="metadata"
                      poster={thumb ?? undefined}
                      className="w-full h-full"
                      src={`${v.streamUrl}?t=${encodeURIComponent(token ?? '')}`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">
                      {v.status === 'error'
                        ? '⚠ Ошибка'
                        : `⏳ ${st.label}${v.pollAttempts ? ` · опрос ${v.pollAttempts}/10` : ''}`}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-sm font-semibold truncate mb-1">{v.topicTitle}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`badge ${st.cls}`}>{st.label}</span>
                    {v.source === 'heygen_import' && <span className="badge badge-violet">из HeyGen</span>}
                    <span className="text-xs text-slate-400">{v.aspectRatio}</span>
                    {v.durationSeconds && <span className="text-xs text-slate-400">{v.durationSeconds}с</span>}
                  </div>
                  {v.errorMessage && (
                    <div className="text-xs text-rose-600 mt-2 bg-rose-50 rounded-lg p-2">{v.errorMessage}</div>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {v.status === 'ready' && !v.isApproved && (
                      <button onClick={() => approve(v.id)} className="btn btn-sm btn-success">✓ Одобрить</button>
                    )}
                    {v.isApproved && (
                      <button onClick={() => openScheduleModal(v.id)} className="btn btn-sm btn-primary">📅 Запланировать</button>
                    )}
                    {v.source !== 'heygen_import' && (v.status === 'ready' || v.status === 'error' || v.status === 'timeout') && (
                      <button onClick={() => regenerate(v.id)} className="btn btn-sm btn-ghost">🔄</button>
                    )}
                    <button onClick={() => remove(v.id)} className="btn btn-sm btn-ghost ml-auto text-rose-600">🗑</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* === Scheduling modal === */}
      {scheduleVideoId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4"
             onClick={() => setScheduleVideoId(null)}>
          <div onClick={(e) => e.stopPropagation()}
               className="card-glass max-w-md w-full p-6 shadow-2xl">
            <h2 className="text-lg font-semibold mb-4">📅 Запланировать публикацию</h2>

            <div className="mb-4">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
                Платформы ({pickedPlatforms.size} выбрано)
              </div>
              <div className="grid grid-cols-2 gap-2">
                {ALL_PLATFORMS.map((p) => {
                  const checked = pickedPlatforms.has(p.key);
                  const available = availablePlatforms.has(p.key);
                  return (
                    <label key={p.key}
                           className={`flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition ring-1 ${
                             checked ? 'bg-indigo-50 ring-indigo-300' : 'bg-white/50 ring-slate-200 hover:bg-slate-50'
                           } ${!available ? 'opacity-50' : ''}`}
                           title={available ? '' : 'Канал не подключён в /settings'}>
                      <input type="checkbox" checked={checked} onChange={() => togglePlatform(p.key)}
                             className="accent-indigo-600" />
                      <span className="text-lg">{p.icon}</span>
                      <span className="text-sm">{p.label}</span>
                      {!available && <span className="ml-auto text-[10px] text-amber-600">⚠</span>}
                    </label>
                  );
                })}
              </div>
              {availablePlatforms.size === 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2 mt-2">
                  Ни одна соц-сеть не подключена. Открой <a href="/settings" className="underline">/settings</a> → подключи через PostProxy.
                </div>
              )}
            </div>

            <div className="mb-4">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Когда публиковать</div>
              <input type="datetime-local" value={scheduleWhen} onChange={(e) => setScheduleWhen(e.target.value)}
                     className="input w-full" />
              <div className="text-xs text-slate-500 mt-1">
                Текущее время = опубликовать прямо сейчас. Будущее = отложить (Bull queue с delay).
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button onClick={() => setScheduleVideoId(null)} className="btn btn-md btn-ghost">Отмена</button>
              <button onClick={submitSchedule} disabled={pickedPlatforms.size === 0}
                      className="btn btn-md btn-primary">
                {scheduleWhen && new Date(scheduleWhen).getTime() > Date.now() + 60_000
                  ? `📅 Запланировать в ${pickedPlatforms.size} сетей`
                  : `🚀 Опубликовать сейчас в ${pickedPlatforms.size} сетей`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
